/**
 * Reasoner transport kernel — hook-free, framework-agnostic.
 *
 * Owns the parts of the /chat send path that are pure infrastructure:
 *   - request body construction (with model-option enrichment)
 *   - encryption (when system config + endpoint policy require it)
 *   - fetch with caller-supplied AbortController
 *   - LLMEventStreamProcessor.processStream invocation, with the turn keyed
 *     by the backend X-Message-Id so a caller can resume it
 *
 * Does NOT manage:
 *   - thread state, message-list mutations, loading flags, error toasts
 *   - the temp user-message UI, currentThreadId, runtime integration
 *
 * Two callers share this kernel so request shape, encryption, and turn keying
 * stay in lockstep (both resume an interrupted turn via
 * streamingRecovery.resumeUntilTerminal):
 *
 *   1. useMessageSender (the React wrapper for the main reasoner UI)
 *   2. the dashboard drill's answer pane (useDrillAnswerSession) — a
 *      one-shot read-only investigation of a card, which owns its own
 *      AbortController + LLMEventStreamProcessor instance
 *
 * Anything that has to live in React state stays in the wrapper; this module
 * only deals with the wire.
 */

import { apiAuthType, apiBaseUrl } from '@/config/env';
import { getReasonerModelOption } from '@/config/models.config';
import {
  encryptRequestPayload,
  shouldApplyEncryptionBasedOnApiConfig,
} from '@/helpers/encryption.helpers';
import type { ReasonerAgentConfig } from '@/interfaces/reasoner.interface';
import type { LLMEventStreamProcessor } from '@/services/LLMEventStreamProcessor';
import { logger } from '@/utils/logger';

/** Output of a single send. Status determines whether the answer is durable. */
export type ReasonerTransportStatus =
  /** Stream completed cleanly — FINISHED received from /chat. Result is durable. */
  | 'finished'
  /** The connection ended without FINISHED (clean close, network error, stream_timeout frame) but the turn goes on server-side: the caller resumes `messageId` (/state → /stream). */
  | 'interrupted'
  /** Stream ended without FINISHED and no messageId was returned, so we can't reconnect. Result is partial. */
  | 'incomplete'
  /** AbortController was triggered by the caller. Caller is responsible for whatever cancellation UX is appropriate. */
  | 'aborted';

export interface ReasonerTransportResult {
  status: ReasonerTransportStatus;
  /** X-Message-Id header from the /chat response, when the server returned one. Needed by callers that want to cache (threadId, messageId) for instant reopen later. */
  messageId: string | null;
}

export interface ReasonerTransportRequest {
  projectId: string;
  /**
   * Thread to send the message into. Caller is responsible for supplying a
   * fresh UUID for new threads — the transport never invents one. The wrapper
   * uses the same id to update React state; the drawer uses it to key its
   * cache entry.
   */
  threadId: string;
  /** First and only user message for this send. Already substituted by the caller. */
  prompt: string;
  /**
   * Agent config for the run. The caller is expected to have already chosen a
   * mode (e.g. via extractPlanIdAndMode) — this kernel does not look at
   * extraMetadata to derive mode, so the wrapper layer keeps that responsibility.
   */
  agentConfig: ReasonerAgentConfig;
  /** Forwarded into the request metadata block verbatim (e.g. plan-id overrides, telemetry flags). */
  extraMetadata?: Record<string, unknown>;
  accessToken: string;
  /**
   * Caller-owned controller; aborting it cancels the live stream.
   */
  controller: AbortController;
  /**
   * Used to decide whether to encrypt the request and to look up the encryptor
   * implementation. `null` / missing means encryption is disabled for this send.
   */
  systemConfig?: { ENCRYPTOR?: unknown } | null;
  /**
   * The SSE consumer. Both callers supply their own instance — sharing one
   * across the main reasoner and the drawer would let stream events from one
   * collide with the other.
   */
  streamProcessor: LLMEventStreamProcessor;
  /** Pre-stream timestamp; passed through to processStream so downstream sync stays correct. */
  timestamp?: number;
  /**
   * Optional callback fired as soon as the backend returns the DB message id in
   * the response headers. Used by callers that may hand the thread off to
   * another UI surface before the stream finishes and before Postgres reflects
   * the streaming assistant row.
   */
  onMessageId?: (messageId: string | null) => void;
}

/** The user's local calendar date and IANA time zone, rendered into the reasoner prompt. */
function buildClientContext(now: Date): {
  current_date: string;
  time_zone: string;
} {
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    current_date: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
    time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

export async function sendReasonerMessage(
  req: ReasonerTransportRequest,
): Promise<ReasonerTransportResult> {
  const {
    projectId,
    threadId,
    prompt,
    agentConfig,
    extraMetadata,
    accessToken,
    controller,
    systemConfig,
    streamProcessor,
    timestamp = Date.now(),
    onMessageId,
  } = req;

  const opt = getReasonerModelOption(agentConfig.model);
  const agentConfigEnriched = {
    ...agentConfig,
    subagent: opt.subagent,
    compaction: opt.compaction,
    tool_max_tokens: opt.tool_max_tokens,
    metadata: opt.metadata,
  };

  const requestBody = {
    messages: [
      { role: 'user', content: [{ type: 'text', text: prompt }] },
    ],
    metadata: {
      projectId,
      agentConfig: agentConfigEnriched,
      thread_id: threadId,
      event_order: 0,
      clientContext: buildClientContext(new Date(timestamp)),
      ...extraMetadata,
    },
  };

  const baseHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'X-Connection-Type': 'stream',
    'X-Auth-Type': apiAuthType,
  };

  const endpoint = `/reasoner/projects/${projectId}/chat`;
  const fullUrl = `${apiBaseUrl}${endpoint}`;

  const encryptor = (systemConfig as { ENCRYPTOR?: unknown } | null | undefined)
    ?.ENCRYPTOR;
  const policyRequiresEncryption = shouldApplyEncryptionBasedOnApiConfig(
    'POST',
    endpoint,
    'request',
  );
  const shouldEncrypt = policyRequiresEncryption && !!encryptor;
  if (policyRequiresEncryption && !encryptor) {
    // Policy says we should encrypt but no encryptor is wired. The request
    // will go out in plaintext, which is almost certainly a misconfiguration.
    // Logging here avoids the silent-downgrade trap that would otherwise hide
    // the issue until a security review noticed it.
    logger.warn(
      '[reasonerTransport] Encryption policy active for /chat but no ENCRYPTOR is configured — sending plaintext.',
    );
  }

  let body: string;
  let encryptionHeaders: Record<string, string> = {};
  if (shouldEncrypt) {
    const encryptionResult = await encryptRequestPayload(
      requestBody,
      // The encryptor is opaque to the transport — pass through to the helper.
      encryptor as Parameters<typeof encryptRequestPayload>[1],
    );
    body = encryptionResult.encryptedData;
    encryptionHeaders = encryptionResult.headers;
  } else {
    body = JSON.stringify(requestBody);
  }

  const response = await fetch(fullUrl, {
    method: 'POST',
    headers: { ...baseHeaders, ...encryptionHeaders },
    body,
    signal: controller.signal,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  streamProcessor.startNewMessage(threadId);

  const messageId = response.headers.get('X-Message-Id');
  onMessageId?.(messageId);
  if (messageId) {
    streamProcessor.startBackendTurn(threadId, messageId);
  }

  try {
    await streamProcessor.processStream(
      response,
      threadId,
      encryptionHeaders['X-Encrypt-Exchange-Key'] || null,
      () => {},
      timestamp,
      (encryptor ?? undefined) as Parameters<
        typeof streamProcessor.processStream
      >[5],
    );
  } catch (error) {
    // A dropped connection leaves the turn running: the caller resumes it.
    if (!messageId || controller.signal.aborted) throw error;
    logger.warn('[reasonerTransport] Stream dropped', error);
  } finally {
    streamProcessor.setMessageIdOverride(null, false, threadId);
  }

  if (streamProcessor.hasReceivedFinished(threadId)) {
    return { status: 'finished', messageId };
  }

  if (controller.signal.aborted) {
    return { status: 'aborted', messageId };
  }

  return { status: messageId ? 'interrupted' : 'incomplete', messageId };
}
